import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import CircularProgressWithLabel from '../Dashboard/CircularProgressWithLabel';

interface CallStatusProps {
  totalCalls?: number;
  callConnected?: number;
  callFailed?: number;
  voicemail?: number;
  others?: number;
  useDarkTheme?: boolean;
}

// Reusable Call Status Dashboard component
function CallStatus({
  totalCalls = 0,
  callConnected = 0,
  callFailed = 0,
  voicemail = 0,
  others = 0,
  useDarkTheme = true,
}: CallStatusProps): React.ReactNode {
  const theme = useTheme();
  const currentTheme = useAlchemystStoreForAi((store) => store.theme);

  // Color configurations inspired by the second image
  const colors = {
    callConnected: '#60A5FA', // blue
    callFailed: '#F87171', // red
    voicemail: '#EC4899', // pink
    others: '#F97316', // orange
    darkBg: '#121212',
    lightBg: '#F8FAFC',
    progress: {
      bg: '#E5E7EB',
      track: '#2DD4BF', // teal for progress
    },
  };

  // Status items configuration for the bottom grid
  const statusItems = [
    {
      label: 'Call Connected',
      value: callConnected,
      color: colors.callConnected,
    },
    { label: 'Call Failed', value: callFailed, color: colors.callFailed },
    { label: 'Voicemail', value: voicemail, color: colors.voicemail },
    { label: 'Others', value: others, color: colors.others },
  ];

  // Calculate the percentage of connected calls
  const connectionPercentage = totalCalls > 0 
    ? Math.round((callConnected / totalCalls) * 100) 
    : 0;
    
  // Determine color based on connection percentage
  const getColor = (percentage: number) => {
    if (percentage >= 80) return '#4ade80'; // Green for high success rate
    if (percentage >= 50) return '#F19937'; // Orange for medium success rate
    return '#f44336'; // Red for low success rate
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 5,
        color: useDarkTheme ? 'white' : 'black',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        border: '0.2rem solid transparent',
        background:
          currentTheme === 'dark'
            ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
            : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
        // background: 'linear-gradient(320deg, rgba(255, 154, 53, 0.125) 1.52%, rgba(0, 0, 0, 0) 90.82%)',
        width: '100%',
        maxWidth: '30vw',
        height: '100%',
      }}
    >
      {/* Main Call Progress Circle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 12,
          mt: 2,
          position: 'relative',
          height: 200,
        }}
      >
        {/* Background circle */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={240}
          thickness={3}
          sx={{
            color: 'rgba(255, 255, 255, 0.1)',
            position: 'absolute',
          }}
        />

        {/* Progress circle - dynamically shows connected calls percentage */}
        <CircularProgressWithLabel
          value={connectionPercentage}
          size={240}
          thickness={3}
          color={getColor(connectionPercentage)}
          sx={{ opacity: '75%' }}
        />

        {/* Center text */}
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            Total Calls
          </Typography>
          <Typography variant="h3">{totalCalls}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {connectionPercentage}% Connected
          </Typography>
        </Box>
      </Box>

      {/* Call Status Grid */}
      <Grid container spacing={2}>
        {statusItems.map((item, index) => (
          <Grid item xs={6} key={index}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: useDarkTheme
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
                display: 'flex',
                flexDirection: 'column',
                height: '10vh',
              }}
            >
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2" color="textSecondary">
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: item.color,
                  }}
                />
              </Box>
              <Typography variant="h5">{item.value}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default CallStatus;
